import { useMemo } from 'react';
import { AutoComplete } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';

/**
 * SHAR-800 — Nationality / Issuing country, filtering as you type.
 *
 * These were plain uppercase text inputs in the admin panel while both apps have
 * had a filtering type-ahead since SHAR-422, so an admin typing a country got no
 * help and no protection against a typo the customer never made.
 *
 * The list is the shared one the apps use, `GET /countries` — static reference
 * data, public, cached for the session. A second hard-coded list in the panel is
 * how the two would come to disagree about how a country is spelled.
 *
 * MATCHED ON THE START OF A WORD, not anywhere in the name, which the ticket
 * asks for and which is the sensible rule: typing "ind" should offer India and
 * Indonesia, not British Indian Ocean Territory. Each word counts as a start, so
 * "kingdom" still finds United Kingdom - somebody typing the distinctive part of
 * a long name should not be punished for not starting at the beginning.
 *
 * The QUERY is tokenised too (Copilot review). The first version treated the
 * whole input as one prefix, so "united k" matched nothing at all: no single
 * word begins with "united k", and the field went blank exactly as the user got
 * to the distinguishing letter. Every token now has to prefix some word, so
 * "united k" keeps United Kingdom and drops United States.
 */
export function CountryAutocomplete({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value?: string;
  onChange?: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const countries = useQuery({
    queryKey: ['countries'],
    // Reference data: fetched once and kept, rather than re-fetched per field.
    staleTime: Infinity,
    queryFn: async () => {
      // `api` is a plain axios instance, so the payload is under `.data`.
      const r = await api.get<{ countries: { code: string; name: string }[] }>('/countries');
      return r.data?.countries ?? [];
    },
  });

  const options = useMemo(() => {
    const all = countries.data ?? [];
    const q = (value ?? '').trim().toLowerCase();
    // No input yet: offer the whole list rather than nothing, so the field is
    // still a picker and not a guessing game.
    const tokens = q.split(/\s+/).filter(Boolean);
    const matches = tokens.length
      ? all.filter((c: { name: string }) => {
          const words = c.name.toLowerCase().split(/[\s(-]+/).filter(Boolean);
          return tokens.every((t) => words.some((w: string) => w.startsWith(t)));
        })
      : all;
    return matches.slice(0, 50).map((c: { name: string }) => ({ value: c.name }));
  }, [countries.data, value]);

  return (
    <AutoComplete
      value={value}
      onChange={(v) => onChange?.(String(v ?? ''))}
      options={options}
      disabled={disabled}
      placeholder={placeholder}
      style={{ width: '100%' }}
      // The list is already narrowed above; antd must not filter it again with
      // its own substring rule, which is the behaviour this ticket is replacing.
      filterOption={false}
    />
  );
}
